import React from 'react';
import type { ActionMeta } from 'react-select';

import { FormikProps } from 'formik';

import useTagCreate from '../../../../hooks/tags/useTagCreate';
// hooks
import useTagsQuery from '../../../../hooks/tags/useTagsQuery';
import useRoleFlags from 'hooks/auth/useRoleFlags';

// models
import { TagModel } from '../../../../models/tag.model';

import { SearchableSelectType } from '../../../inputs/searchableSelect';
// components
import AampeCreateSelectDropdown from '../../../shared/AampeCreateSelectDropdown/AampeCreateSelectDropdown';
import { FormValues } from './helper';

interface TagsInputProps {
    formikProps: FormikProps<FormValues>;
    label?: string;
    menuPortalId?: string;
    placeholder?: string;
    isFormSubmitAllowed?: boolean;
    isDisabled?: boolean;
}

const TagsInput: React.FC<React.PropsWithChildren<TagsInputProps>> = ({
    formikProps,
    label,
    menuPortalId = '',
    placeholder = 'Select or create a tag',
    isFormSubmitAllowed,
    isDisabled,
}) => {
    const { hasWriteAccess } = useRoleFlags();
    const { data, isPending: isLoading } = useTagsQuery();
    const createTag = useTagCreate();
    const tagsList = data;

    const { values, errors, touched, setFieldValue, setFieldTouched } =
        formikProps;

    const handleTagChange = (
        name: string,
        value: SearchableSelectType[],
        actionMeta?: ActionMeta<SearchableSelectType>,
    ) => {
        if (actionMeta?.action === 'remove-value') {
            const removedValue = actionMeta.removedValue;
            setFieldValue(
                'tags',
                values.tags?.filter(
                    (item) => item.value !== removedValue.value,
                ),
            );
        } else if (actionMeta?.action === 'clear') {
            setFieldValue('tags', []);
        } else if (actionMeta?.action === 'create-option') {
            const newTag = actionMeta.option;
            createTag.mutate(
                {
                    name: newTag.label,
                },
                {
                    onSuccess: (res) => {
                        value.pop();
                        value.push({
                            label: newTag.label,
                            value: res.data?.id,
                        });
                        setFieldValue('tags', value);
                        if (isFormSubmitAllowed) {
                            formikProps.handleSubmit();
                        }
                    },
                },
            );
        } else {
            setFieldValue('tags', value);
        }
        if (isFormSubmitAllowed && actionMeta?.action !== 'create-option') {
            formikProps.handleSubmit();
        }
    };

    const menuPortal = document.getElementById(menuPortalId);

    return (
        <AampeCreateSelectDropdown
            label={label}
            options={(tagsList || []).map((tag: TagModel) => ({
                value: tag.id,
                label: tag.name,
            }))}
            name={'tags'}
            value={values.tags}
            onChange={handleTagChange as any}
            onBlur={setFieldTouched}
            error={errors.tags}
            touched={touched.tags}
            placeholder={placeholder}
            isLoading={isLoading}
            isMultiple
            isDisabled={isDisabled}
            creatable={hasWriteAccess}
            menuPortalTarget={menuPortal}
            infoDescription={
                <span>
                    Tags are any additional information that helps you organize
                    your catalog.{' '}
                    <strong>Tags are only used for your convenience.</strong>{' '}
                    They will not be used in learning or visible to your
                    customers.
                </span>
            }
        />
    );
};

export default TagsInput;
